import { Component, OnInit, ViewChild, inject, signal, computed, DestroyRef } from '@angular/core';
import { RouterModule, RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { OrderWebhookService } from '../../services/order-webhook.service';
import { ToastNotificationComponent } from '../toast-notification/toast-notification.component';
import { VersionInfoComponent } from '../version-info/version-info.component';
import { VersionService } from '../../services/version.service';
import { Observable, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';

interface BreadcrumbSegment {
  label: string;
  path: string;
}

@Component({
  selector: 'dashboard',
  standalone: true,
  imports: [RouterOutlet, RouterModule, AsyncPipe, ToastNotificationComponent, VersionInfoComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
})
export class DashboardComponent implements OnInit {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private orderWebhookService = inject(OrderWebhookService);
  private versionService = inject(VersionService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  // Role observables — preserved exactly
  isAdmin$: Observable<boolean> = this.authService.isAdmin$;
  isShopeeDocsTeam$: Observable<boolean> = this.authService.isShopeeDocsTeam$;
  isShopeeChecker$: Observable<boolean> = this.authService.isShopeeChecker$;
  isWarehouse$: Observable<boolean> = this.authService.isWarehouse$;
  isForwardingAdmin$: Observable<boolean> = this.authService.isForwardingAdmin$;
  isForwardingUser$: Observable<boolean> = this.authService.isForwardingUser$;

  // Sidebar collapse — signal for reactivity
  isDashboardCollapsed = signal(false);

  // Version
  @ViewChild(VersionInfoComponent) versionModal!: VersionInfoComponent;
  appVersion: string = this.versionService.getVersionString();

  // Breadcrumb
  breadcrumbs = signal<BreadcrumbSegment[]>([]);

  // Username display
  displayUsername = signal<string>('');

  // User role display — sourced from AuthService userRole$ observable via signal
  displayRole = signal<string>('');

  // User initials — computed signal, up to two chars from first/last name tokens
  userInitials = computed((): string => {
    const name = this.displayUsername();
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/).filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  });

  constructor() {
    // Breadcrumb auto-generation from route
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(event => {
      this.breadcrumbs.set(this.buildBreadcrumbs(event.urlAfterRedirects));
    });
  }

  ngOnInit(): void {
    this.notificationService.initialize();
    this.orderWebhookService.initialize();

    const username = this.authService.getUsername();
    this.displayUsername.set(username ?? '');

    // Populate role caption — subscribe to observable and keep signal in sync
    this.authService.userRole$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
      this.displayRole.set(role ?? '');
    });

    // Set initial breadcrumbs
    this.breadcrumbs.set(this.buildBreadcrumbs(this.router.url));
  }

  private buildBreadcrumbs(url: string): BreadcrumbSegment[] {
    const segments: BreadcrumbSegment[] = [{ label: 'Home', path: '/dashboard' }];
    const clean = url.split('?')[0].replace('/dashboard/', '').replace('/dashboard', '');
    if (!clean) return segments;

    const parts = clean.split('/');
    const labelMap: Record<string, string> = {
      forwarding: 'Forwarding',
      quotations: 'Quotations',
      inquiries: 'Inquiries',
      dashboard: 'Dashboard',
      vendors: 'Vendors',
      ports: 'Ports',
      incoterms: 'Incoterms',
      'container-load-types': 'Container Load Types',
      charges: 'Charges',
      rates: 'Rates',
      'markup-config': 'Client Markups',
      'quotation-templates': 'Quotation Templates',
      settings: 'Settings',
      'exchange-rates': 'Exchange Rates',
      'charge-basis': 'Charge Basis',
      'marine-insurance': 'Marine Insurance Config',
      admin: 'Admin',
      shopee: 'Shopee Dashboard',
      orders: 'Orders',
      'pre-alerts': 'Pre-Alerts',
      manifest: 'Manifest',
      'customs-clearance-progress': 'Customs Clearance Progress',
      'mobile-scanner': 'Mobile Scanner',
      'user-preferences': 'User Preferences',
      'unit-bases': 'Unit Bases',
      'customs-processes': 'Customs Brokerage',
      'user-management': 'User Management',
      'template-management': 'XML Templates',
      'integration-management': 'Integration Management',
      'system-health': 'System Health',
      new: 'New',
    };

    let path = '/dashboard';
    for (const part of parts) {
      if (!part) continue;
      path += '/' + part;
      const label = labelMap[part] ?? this.titleCase(part);
      segments.push({ label, path });
    }
    return segments;
  }

  private titleCase(s: string): string {
    return s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  toggleDashboard(): void {
    this.isDashboardCollapsed.update(v => !v);
  }

  openVersionModal(): void {
    if (this.versionModal) {
      this.versionModal.open();
    }
  }

  navigateToPreferences(): void {
    this.router.navigate(['/dashboard/user-preferences']);
  }

  logout(): void {
    this.authService.logout();
  }
}
